/**
 * 
 * import 할거없는 단순 함수
 * 
 */

import { endOfMonth, format, startOfMonth } from "date-fns";

const calculateRemToPx = (rem) => {
    const fontSize = getComputedStyle(document.documentElement).fontSize;
    return rem * parseFloat(fontSize);
};

export { calculateRemToPx };

// =====================================
// 휴일 표시 포맷 (기초 공휴일)
// =====================================
/** 일자 → mm-dd 형태 (예: 20250101 → 01-01, 0101 → 01-01) */
const formatHdayDate = (val) => {
    if (val == null || val === '') return '-';
    const s = String(val).replace(/[-./]/g, '');
    if (s.length >= 4) {
        const mm = s.length >= 8 ? s.slice(4, 6) : s.slice(0, 2);
        const dd = s.length >= 8 ? s.slice(6, 8) : s.slice(2, 4);
        return `${mm}-${dd}`;
    }
    return val;
};

/** 요일 숫자 → 한글 (1=월, 2=화, 3=수, 4=목, 5=금, 6=토, 7=일) */
const formatHdayDweek = (val) => {
    const map = { 1: '월', 2: '화', 3: '수', 4: '목', 5: '금', 6: '토', 7: '일' };
    if (val == null || val === '') return '-';
    const n = Number(val);
    return map[n] ?? val;
};

export { formatHdayDate, formatHdayDweek };

// =====================================
// 시간 계산하기
// =====================================

const addTimeStrings = (time1, time2) => {
    const [h1, m1, s1] = time1.split(':').map(Number);
    const [h2, m2, s2] = time2.split(':').map(Number);
    
    let totalSeconds = (h1 * 3600 + m1 * 60 + s1) + (h2 * 3600 + m2 * 60 + s2);
    
    const hours = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${hours}:${minutes}:${seconds}`;
};

// 시간 문자열을 빼는 함수
const subtractTimeStrings = (time1, time2) => {
    const [h1, m1, s1] = time1.split(':').map(Number);
    const [h2, m2, s2] = time2.split(':').map(Number);
    
    let totalSeconds1 = h1 * 3600 + m1 * 60 + s1;
    let totalSeconds2 = h2 * 3600 + m2 * 60 + s2;

    let diffSeconds = totalSeconds1 - totalSeconds2;

    if (diffSeconds < 0) {
        return "00:00:00"; // 음수일 경우 0으로 처리
    }

    const hours = Math.floor(diffSeconds / 3600);
    diffSeconds %= 3600;
    const minutes = Math.floor(diffSeconds / 60);
    const seconds = diffSeconds % 60;

    return `${hours}:${minutes}:${seconds}`;
}

export { addTimeStrings, subtractTimeStrings };

const getMonthStrEndDateList = (date) => {
    // 오늘 날짜의 월을 'yyyy-MM' 형식으로 포맷
    const dateMonth = format(date, 'yyyy-MM');

    // 월의 시작일 구하기
    const startOfCurrentMonth = startOfMonth(date);

    // 월의 마지막일 구하기
    const endOfCurrentMonth = endOfMonth(date);

    // console.log(`월의 시작일: ${startOfCurrentMonth}`);
    // console.log(`월의 마지막일: ${endOfCurrentMonth}`);
    return { dateMonth, startOfCurrentMonth, endOfCurrentMonth };
}

export { getMonthStrEndDateList };


const convertTimeStringToDate = (timeString) => {
    if (!timeString){
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    } ;
    const [hours, minutes, seconds] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    date.setSeconds(parseInt(0, 10));
    return date;
};

export { convertTimeStringToDate };


// =====================================
// 폰트 크기 자동 조정 함수
// =====================================

/**
 * 테이블의 넘치는 셀들의 폰트 크기를 자동으로 조정하는 함수
 * @param {HTMLElement} table - 대상 테이블 요소
 * @param {Document} frameDoc - iframe의 document 객체
 * @param {string} cellSelector - 대상 셀 선택자 (기본값: 'td.amount-cell, td.amount-item-name-cell')
 * @param {number} originalFontSize - 기본 폰트 크기 (기본값: 12)
 * @param {number} minFontSize - 최소 폰트 크기 (기본값: 8)
 */
const applyAutoShrinkToOverflowingCells = (table, frameDoc, cellSelector = 'td.amount-cell, td.amount-item-name-cell', originalFontSize = 12, minFontSize = 8) => {
    // iframe의 window 객체에 안전하게 접근
    let frameWindow;
    try {
        // iframe의 contentWindow에 직접 접근
        frameWindow = frameDoc.contentWindow;
        if (!frameWindow) {
            console.warn('Frame window not available, using parent window');
            frameWindow = window;
        }
    } catch (error) {
        console.warn('Error accessing frame window, using parent window:', error);
        frameWindow = window;
    }

    const cells = table.querySelectorAll(cellSelector);

    // measureText 함수 추가 (canvas 활용)
    const measureText = (text, fontSize, fontFamily = 'inherit') => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        
        // 숫자 패턴인지 확인 (숫자, 쉼표, 마이너스, 소수점만 포함)
        const isNumeric = /^[0-9,\-\.]+$/.test(text.trim());
        
        if (isNumeric) {
            // 숫자의 경우: 폰트크기 * 글자수 / 4로 너비 계산
            const charCount = text.trim().length;
            const calculatedWidth = fontSize * charCount / 4;
            
            // 로그 제거 (숫자 계산)
            
            return {
                width: calculatedWidth,
                height: fontSize * 1.2, // line-height 1.2 기준
                isNumeric: true,
                actualFontSize: fontSize,
                charCount: charCount,
                calculatedWidth: calculatedWidth
            };
        } else {
            // 숫자가 아닌 경우: canvas로 실제 측정
            context.font = `${fontSize}px ${fontFamily}`;
            const metrics = context.measureText(text);
            return {
                width: metrics.width,
                height: fontSize * 1.2, // line-height 1.2 기준
                isNumeric: false,
                actualFontSize: fontSize
            };
        }
    };

    cells.forEach(cell => {
        try {
            // iframe 내에서 computedStyle 계산 (안전한 접근)
            let targetWindow;
            try {
                targetWindow = frameDoc.defaultView || frameDoc.parentWindow || window;
            } catch (e) {
                console.warn('Could not access iframe window, using parent window');
                targetWindow = window;
            }
            const computedStyle = targetWindow.getComputedStyle(cell);
            const fontFamily = computedStyle.fontFamily || 'inherit';
            
            // iframe 내에서 패딩 값 추출
            const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
            const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
            const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
            const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
            
            // iframe 내에서 테이블 셀의 실제 너비 측정
            const rect = cell.getBoundingClientRect();
            const containerWidth = rect.width - paddingLeft - paddingRight;
            const containerHeight = rect.height - paddingTop - paddingBottom;
            
            // 컨테이너 크기 디버깅 로그 제거
            const childElements = cell.querySelectorAll('p, div, span');

            if (childElements.length > 0) {
                // 자식 요소별로 개별적으로 처리
                childElements.forEach((element, index) => {
                    let elementFontSize = originalFontSize;
                    // 자식이 두개 이상이고 마지막 자식 요소인 경우에만 85% 적용
                    if (childElements.length >= 2 && index === childElements.length - 1) {
                        elementFontSize = Math.max(minFontSize, originalFontSize * 0.85);
                    }
                    // 초기 폰트 크기 적용
                    element.style.fontSize = `${elementFontSize}px`;
                    element.style.lineHeight = '1.2';
                    element.style.wordBreak = 'break-word';

                    const text = element.textContent.trim();
                    if (!text) return;

                    // 먼저 현재 폰트 크기로 fit되는지 확인
                    const measureResult = measureText(text, elementFontSize, fontFamily);
                    const { width: initialWidth, height: initialHeight, isNumeric } = measureResult;

                    if (initialWidth <= containerWidth && initialHeight <= containerHeight) {
                        // 이미 맞으면 그대로 사용
                        element.style.fontSize = `${Math.floor(elementFontSize)}px`;
                        console.log(`텍스트: ${text} / 셀너비: ${containerWidth} / 변경폰트크기: ${Math.floor(elementFontSize)} / 폰트크기에따른너비: ${initialWidth}`);
                    } else {
                        // 맞지 않으면 폰트 크기를 줄여가며 찾기
                        let currentFontSize = elementFontSize;
                        let attempts = 0;
                        const maxAttempts = 40;
                        
                        while (currentFontSize > minFontSize && attempts < maxAttempts) {
                            attempts++;
                            currentFontSize -= 1;
                            const { width, height } = measureText(text, currentFontSize, fontFamily);
                            
                            if (width <= containerWidth && height <= containerHeight) {
                                break;
                            }
                        }
                        
                        currentFontSize = Math.max(minFontSize, currentFontSize);
                        element.style.fontSize = `${Math.floor(currentFontSize)}px`;
                        const finalWidth = measureText(text, currentFontSize, fontFamily).width;
                        console.log(`텍스트: ${text} / 셀너비: ${containerWidth} / 변경폰트크기: ${Math.floor(currentFontSize)} / 폰트크기에따른너비: ${finalWidth}`);
                    }
                });
            } else {
                // 셀 자체에 대해 처리
                let cellFontSize = originalFontSize;
                cell.style.fontSize = `${cellFontSize}px`;
                cell.style.lineHeight = '1.2';
                cell.style.wordBreak = 'break-word';
                const text = cell.textContent.trim();
                if (!text) return;
                // 먼저 현재 폰트 크기로 fit되는지 확인
                const measureResult = measureText(text, cellFontSize, fontFamily);
                const { width: initialWidth, height: initialHeight, isNumeric } = measureResult;

                if (initialWidth <= containerWidth && initialHeight <= containerHeight) {
                    // 이미 맞으면 그대로 사용
                    cell.style.fontSize = `${Math.floor(cellFontSize)}px`;
                    console.log(`텍스트: ${text} / 셀너비: ${containerWidth} / 변경폰트크기: ${Math.floor(cellFontSize)} / 폰트크기에따른너비: ${initialWidth}`);
                } else {
                    // 맞지 않으면 폰트 크기를 줄여가며 찾기
                    let currentFontSize = cellFontSize;
                    let attempts = 0;
                    const maxAttempts = 40;
                    
                    while (currentFontSize > minFontSize && attempts < maxAttempts) {
                        attempts++;
                        currentFontSize -= 1;
                        const { width, height } = measureText(text, currentFontSize, fontFamily);
                        
                        if (width <= containerWidth && height <= containerHeight) {
                            break;
                        }
                    }
                    
                    currentFontSize = Math.max(minFontSize, currentFontSize);
                    cell.style.fontSize = `${Math.floor(currentFontSize)}px`;
                    const finalWidth = measureText(text, currentFontSize, fontFamily).width;
                    console.log(`텍스트: ${text} / 셀너비: ${containerWidth} / 변경폰트크기: ${Math.floor(currentFontSize)} / 폰트크기에따른너비: ${finalWidth}`);
                }
            }
        } catch (error) {
            console.warn('Error processing cell:', error);
        }
    });
};

export { applyAutoShrinkToOverflowingCells };

// =====================================
// User-Agent 기반 모바일 감지 함수
// =====================================

/**
 * User-Agent를 기반으로 모바일 기기인지 확인하는 함수
 * @returns {boolean} 모바일 기기이면 true, 아니면 false
 */
const isMobileDevice = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // 모바일 기기 패턴 체크
    const mobilePatterns = [
        /Android/i,
        /webOS/i,
        /iPhone/i,
        /iPad/i,
        /iPod/i,
        /BlackBerry/i,
        /Windows Phone/i,
        /Mobile/i
    ];
    
    return mobilePatterns.some(pattern => pattern.test(userAgent));
};

export { isMobileDevice };